/**
 * Data-driven industry landing pages for programmatic SEO.
 * Add an industry here → a new crawlable /solutions/<slug> page + sitemap entry.
 * Keep it accurate to what AgentMesh agents actually do (reason + use tools:
 * HTTP, email, Slack, webhooks, Google Calendar; run on schedules/triggers).
 */

export interface AgentSpec {
  name: string;
  does: string;
  /**
   * The tools this agent needs enabled, and which of them need an integration
   * connected first. Added 12 Sept 2026: the pages listed three agent names per
   * industry and never said what you had to connect to make one work, which is
   * the first question anyone reading them actually has. Names must match the
   * registry in services/tools/registry.py.
   */
  tools: string;
}

export interface Industry {
  slug: string;
  name: string;
  /** <title> / H1 focus. */
  title: string;
  metaDescription: string;
  intro: string;
  pains: string[];
  team: AgentSpec[];
  faqs: { q: string; a: string }[];
}

export const industries: Industry[] = [
  {
    slug: 'retail',
    name: 'Retail',
    title: 'AI Agents for Retail',
    metaDescription:
      'AgentMesh for retail: no-code AI agents that answer customers, watch inventory, and follow up on orders — working together and running on their own.',
    intro:
      'Retail runs on many small, repetitive decisions across support, inventory, and orders. AgentMesh lets you build a team of AI agents that handle them — reasoning over each case, calling your systems, and escalating only when needed.',
    pains: [
      'Support tickets pile up faster than staff can answer',
      'Stockouts and overstock because nobody is watching inventory in real time',
      'Order and shipping questions eat hours every day',
    ],
    team: [
      { name: 'Support Agent', does: 'Answers product, order and returns questions; escalates edge cases', tools: '`http_request` to read orders · `send_email` or `slack_message` to escalate' },
      { name: 'Inventory Watcher', does: 'Checks stock via your API on a schedule and alerts on low/overstock (Slack/email)', tools: '`http_request` to your stock API · `slack_message` or `send_email` · a cron schedule' },
      { name: 'Order Follow-up', does: 'Drafts shipping updates and post-purchase follow-ups', tools: '`http_request` for order state · `send_email`' },
    ],
    faqs: [
      {
        q: 'Do I need engineers to set this up?',
        a: 'No. AgentMesh is a no-code visual builder. Start from a retail template or drag agents onto a canvas — if you can describe the flow, you can build it.',
      },
      {
        q: 'Can agents connect to my store or systems?',
        a: 'Yes — agents use HTTP-request and webhook tools to call any REST API (SSRF-guarded), plus Slack, email and Google Calendar integrations.',
      },
    ],
  },
  {
    slug: 'hospitality',
    name: 'Hospitality',
    title: 'AI Agents for Hospitality',
    metaDescription:
      'AgentMesh for hospitality: no-code AI agents that handle guest questions, bookings and follow-ups — coordinated and running on schedules.',
    intro:
      'Hotels, restaurants and venues live and die on responsiveness. AgentMesh builds a team of AI agents that answer guests, manage bookings, and run the routine follow-ups — freeing your staff for the guests in front of them.',
    pains: [
      'Guest messages come in around the clock across channels',
      'Bookings and schedule changes are managed by hand',
      'No time for review requests and post-stay follow-ups',
    ],
    team: [
      { name: 'Concierge Agent', does: 'Answers guest questions and requests; hands off to staff when needed', tools: 'None required · `slack_message` to hand off to staff' },
      { name: 'Booking Coordinator', does: 'Checks availability and creates/updates events on Google Calendar', tools: '`google_calendar_list_events` and `google_calendar_create_event` (OAuth)' },
      { name: 'Follow-up Agent', does: 'Sends confirmations and review requests on a schedule', tools: '`send_email` · a cron schedule' },
    ],
    faqs: [
      {
        q: 'Can it manage our calendar/bookings?',
        a: 'Yes — the Google Calendar integration (OAuth) lets agents read availability and create events, and cron schedules run follow-ups automatically.',
      },
      {
        q: 'How fast can we launch?',
        a: 'Start from a hospitality template and adjust — most teams have a working agent in minutes, not weeks.',
      },
    ],
  },
  {
    slug: 'professional-services',
    name: 'Professional Services',
    title: 'AI Agents for Professional Services',
    metaDescription:
      'AgentMesh for professional services: no-code AI agents that qualify leads, schedule, and prep client work — orchestrated end to end.',
    intro:
      'Agencies, consultancies and firms lose billable time to intake, scheduling and admin. AgentMesh builds AI agents that triage inquiries, book meetings, and prep the busywork so your experts stay on client work.',
    pains: [
      'Inbound leads sit unqualified and go cold',
      'Scheduling back-and-forth wastes senior time',
      'Repetitive research and document prep before every engagement',
    ],
    team: [
      { name: 'Intake Agent', does: 'Qualifies inbound inquiries and routes them with a summary', tools: 'None required · `slack_message`, `send_email` or `telegram_message` to route' },
      { name: 'Scheduler', does: 'Proposes and books meetings via Google Calendar', tools: '`google_calendar_list_events` and `google_calendar_create_event` (OAuth)' },
      { name: 'Prep Agent', does: 'Gathers context and drafts briefs ahead of client calls', tools: '`http_request` to your CRM or notes · `notion_create_page` if the brief lives there' },
    ],
    faqs: [
      {
        q: 'Can agents hand off to each other?',
        a: 'Yes — AgentMesh orchestrates multi-agent workflows (sequential, parallel, conditional, fan-out, DAG) with structured hand-offs between agents.',
      },
      {
        q: 'Is our data isolated?',
        a: 'Yes — tenant isolation, encryption at rest, signed JWT auth and rate limiting; you can bring and rotate your own LLM keys.',
      },
    ],
  },
  {
    slug: 'manufacturing',
    name: 'Manufacturing',
    title: 'AI Agents for Manufacturing',
    metaDescription:
      'AgentMesh for manufacturing: no-code AI agents that monitor operations, triage issues, and coordinate suppliers — automated and always on.',
    intro:
      'Manufacturing depends on staying ahead of problems. AgentMesh builds AI agents that watch your systems on a schedule, triage issues, and coordinate the follow-through with suppliers and teams.',
    pains: [
      'Issues surface late because no one is watching data continuously',
      'Supplier and PO coordination is manual and error-prone',
      'Reporting eats time that should go to the floor',
    ],
    team: [
      { name: 'Ops Monitor', does: 'Polls your systems on a schedule and flags anomalies (Slack/email)', tools: '`http_request` to your systems · `slack_message` or `send_email` · a cron schedule' },
      { name: 'Issue Triage', does: 'Classifies incidents and routes them with context', tools: 'None required · `slack_message` or `send_email` to route' },
      { name: 'Supplier Coordinator', does: 'Drafts supplier updates and follow-ups via email/webhook', tools: '`send_email` · `send_webhook` (needs an outbound webhook configured)' },
    ],
    faqs: [
      {
        q: 'Can agents run automatically?',
        a: 'Yes — cron schedules run workflows on a timetable, and inbound webhook triggers let external events kick off an agent run.',
      },
      {
        q: 'Can it call our MES/ERP?',
        a: 'Agents call any REST API via the HTTP-request tool (SSRF-guarded), so they can read/write your systems where an API exists.',
      },
    ],
  },
  {
    slug: 'healthcare',
    name: 'Healthcare',
    title: 'AI Agents for Healthcare Operations',
    metaDescription:
      'AgentMesh for healthcare operations: no-code AI agents that handle scheduling, intake and follow-ups — coordinated and secure.',
    intro:
      'Healthcare operations teams drown in scheduling, intake and follow-up. AgentMesh builds AI agents that handle the operational load — coordinating appointments, prepping intake, and running reminders — so staff focus on patients.',
    pains: [
      'Appointment scheduling and reminders are manual',
      'Intake paperwork and routing slow everything down',
      'No-shows and missed follow-ups hurt outcomes and revenue',
    ],
    team: [
      { name: 'Scheduling Agent', does: 'Coordinates appointments via Google Calendar and sends reminders', tools: '`google_calendar_list_events` and `google_calendar_create_event` (OAuth) · `send_email`' },
      { name: 'Intake Assistant', does: 'Collects and structures intake info and routes it', tools: 'None required · `http_request` to write the record into your system' },
      { name: 'Follow-up Agent', does: 'Runs reminder and follow-up sequences on a schedule', tools: '`send_email` · a cron schedule' },
    ],
    faqs: [
      {
        q: 'Is AgentMesh HIPAA compliant?',
        a: 'AgentMesh is security-first (tenant isolation, encryption at rest, JWT auth, rate limiting) and GDPR-aware, but it is not SOC 2 or HIPAA certified. Evaluate it for your compliance needs before handling PHI.',
      },
      {
        q: 'Can we control what agents can access?',
        a: 'Yes — tools are allow-listed per agent, outbound calls go through an SSRF guard, and secrets/keys are encrypted per user.',
      },
    ],
  },
];

export function getIndustry(slug: string): Industry | undefined {
  return industries.find((i) => i.slug === slug);
}
